import { useRef, useState } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import { DirtyHint } from '../../../../components/common/DirtyHint';

interface Props {
  title: string;
  /** 수정 모드 — 무변경 저장을 막는다(추가 모드는 기존 동작 유지). */
  isEdit?: boolean;
  initialName?: string;
  onSubmit: (name: string) => Promise<void>;
  onClose: () => void;
}

/**
 * 공종/공정 추가/수정 폼 모달.
 * 이름 입력 + Enter 제출 + 에러 표시 + 저장중 비활성화.
 */
export function ProcessTypeFormModal({ title, isEdit = false, initialName = '', onSubmit, onClose }: Props) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 열림 시 이름 입력에 포커스 — `autoFocus`는 쓰지 않는다: base Modal이 마운트 effect에서
  // 포커스를 dialog 컨테이너로 옮기므로 autoFocus가 커밋 직후 덮인다. initialFocusRef가 그 자리다.
  const nameInputRef = useRef<HTMLInputElement>(null);

  // 입력이 이름 하나뿐이라 스냅샷 훅 없이 직접 비교한다.
  const isUnchanged = isEdit && name.trim() === initialName.trim();

  // 진행 중 재진입 가드 — state(`submitting`)는 다음 렌더에야 보이므로 같은 틱의 두 번째
  // Enter를 막지 못한다. ref는 즉시 바뀐다(Enter 두 번 → 공종 두 건 생성을 막는 자리).
  const inFlightRef = useRef(false);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    // 버튼 disabled를 우회하는 경로(입력 Enter)까지 무변경 저장·중복 저장을 막는다.
    if (!trimmed || isUnchanged || inFlightRef.current) return;
    inFlightRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장에 실패했습니다.');
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  // 부모가 열 때만 마운트하므로 open은 늘 true다. 저장 중에는 닫는 길 전부를 막는다
  // (종전에도 오버레이 클릭이 `!submitting`일 때만 닫았다).
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      initialFocusRef={nameInputRef}
      closeOnOverlay={!submitting}
      closeOnEsc={!submitting}
      showCloseButton={!submitting}
      closeLocked={submitting}
      footer={
        <>
          {/* 저장이 왜 막혔는지 보이지 않으면 "버튼이 죽었다"로 읽힌다 — 이유를 같이 띄운다. */}
          {isEdit && (
            <DirtyHint isDirty={!isUnchanged} className="mr-auto self-center text-sm" />
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!name.trim() || submitting || isUnchanged}
          >
            {submitting ? '저장 중...' : initialName ? '수정' : '추가'}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 rounded-card bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-ink-700 mb-1">
          공종/공정명 <span className="text-red-500">*</span>
        </label>
        <input
          ref={nameInputRef}
          type="text"
          placeholder="예: 도장 공사, 용접 작업, 전기 공사"
          value={name}
          onChange={(e) => setName(e.target.value)}
          readOnly={submitting}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          className="w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
        />
      </div>
    </Modal>
  );
}
